<template>
  <div class="">
    <div class="flex justify-between">
      <inline-tab-header
          :headers="filterHeaders"
          name="Filter By"
          :current="activeHeaderFilter"
          @update:current="updateCurrentFilter"
      ></inline-tab-header>
    </div>

    <!-- Table -->
    <ajax-table :data="data"
                :headers="headers"
                v-model:loading="ajaxTable.loading"
                v-model:search="ajaxTable.search"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                v-model:page="ajaxTable.page"
                v-model:items-per-page="ajaxTable.itemsPerPage"
                v-model:page-info="ajaxTable.pageInfo"
                :exportable="false"
                @loadData="getData"
    >
      <!-- Create Alarm Profile Template Button Right of Search -->
      <template #search-right>
        <div    v-if="permissions.can('edit_alarm_profiles')"
                class="self-start flex items-center w-full md:w-auto ml:0 md:ml-2">
          <green-button  :href="company.buildCompanyRoute('alarm-profiles/create')"
          >
            <div    class="inline-flex items-center">
              <span   class="lnr icon-plus-circle mr-2"></span>
              <span>Create Alarm Profile</span>
            </div>
          </green-button>
        </div>
      </template>

      <!-- Column 1: Alarm Profile Name-->
      <template #col-1="props">
        <div class="flex flex-col items-start justify-center">
            <span class="my-1 text-gray-700">
                {{ props.row.name }}
            </span>
            <span v-if="permissions.isSonicu() || permissions.isSonicuViewOnly()" class="text-sm text-gray-500 italic">
               Last Updated: {{ props.row.updated_at }}
            </span>
        </div>
      </template>

        <!-- Alarm Thresholds Column -->
        <template #col-2="props">
            <div class="flex flex-col items-center justify-center py-2 space-y-1">

                <div v-if="alarmProfileThresholdHigh(props.row) || alarmProfileCautionThresholdHigh(props.row)" class="flex flex-col items-center">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-0.5">
                        {{ alarmProfileValueString(alarmProfileThresholdHigh(props.row), alarmProfileCautionThresholdHigh(props.row)) }}
                    </span>
                    <div class="lnr icon-exit-up2 text-red-800 text-sm"></div>
                </div>

                <span class="px-2 py-0.5 rounded-full"
                      :class="props.row.threshold_alarms_enabled ? 'bg-green-100' : 'bg-red-100'">
                          {{ props.row.threshold_alarms_enabled ? 'Enabled' : 'Disabled' }}
                </span>

                <div v-if="alarmProfileThresholdLow(props.row) || alarmProfileCautionThresholdLow(props.row)" class="flex flex-col items-center">
                    <div class="lnr icon-exit-down2 text-blue-800 text-sm"></div>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-0.5">
                        {{ alarmProfileValueString(alarmProfileThresholdLow(props.row), alarmProfileCautionThresholdLow(props.row)) }}
                    </span>
                </div>

            </div>
        </template>

      <!-- Column 3: Offline Alarm-->
      <template #col-3="props">
        <div class="">
          <div class="flex justify-center items-center py-2">
                            <span   class="px-2 my-2 rounded-full"
                                    :class="props.row.offline_alarms_enabled ? 'bg-green-100' : 'bg-red-100'">
                                {{ props.row.offline_alarms_enabled ? 'Enabled' : 'Disabled' }}
                            </span>
            <div class="flex justify-center my-2">
            </div>
          </div>
        </div>
      </template>

      <!-- Column 4: Meter count of meters affected by alarm Profile -->
      <template #col-4="props">
        <div    class="flex flex-row items-center">
                    <span   class="my-1 text-gray-700">
                        {{ props.row.meter_count }}
                    </span>

                    <span   v-if="permissions.isPartner() && props.row.meter_count_with_default > 0"
                            class="ml-2 text-gray-800 px-2.5 py-0.5 rounded-full bg-gray-100">
                        ({{ props.row.meter_count_with_default }} hidden)
                    </span>
        </div>
      </template>

      <!-- Column 5: View/Edit/Delete Actions on Alarm Profile -->
        <template #col-5="props">
            <div class="flex justify-end space-x-4 text-gray-700 w-full">
                <span
                    @click="viewAlarmProfileLink(props.row.id)"
                    class="lnr icon-magnifier hover:text-gray-900 cursor-pointer"
                ></span>
                <span
                    @click="editAlarmProfileLink(props.row.id)"
                    class="lnr icon-pencil hover:text-gray-900 cursor-pointer"
                ></span>
                <span
                    @click="askToDelete(props.row.id, props.row.meter_count)"
                    class="lnr icon-trash2 hover:text-gray-900 cursor-pointer"
                ></span>
            </div>
        </template>
    </ajax-table>
    <!--    Notification modal for confirmation, if needed -->
    <notification-modal
        v-model:is-open="showConfirmModal"
        :give-option=true
        :is-error-modal="true"
        @action="(val) => val ? trash() : showConfirmModal=false"
    >
      <template #title-content>
        Are you sure?
      </template>
      Would you really like to delete this alarm profile?
    </notification-modal>

    <!--    Notification modal for the results -->
    <notification-modal
        v-model:is-open="showResultModal"
        @close="getData()"
    >
      <template #title-content>
        Deletion Successful!
      </template>
      {{ modalText }}
    </notification-modal>

    <!--    Error modal when deleting a profile with points assigned. -->
    <notification-modal
        :is-error-modal="true"
        v-model:is-open="showErrorModal"
    >
      <template #title-content>
        Cannot Delete Alarm Profile
      </template>
      Cannot delete Alarm Profile with points assigned.
    </notification-modal>
    <notification-modal v-model:is-open="showFailedToDeleteModal"
                        :is-error-modal="true"
    >
        <template #title-content>
            Failed to Delete Alarm Profile
        </template>
        {{ modalText }}
    </notification-modal>
  </div>
</template>
<script>

import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Pagination from "@/components/Pagination"
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect"
import FloatingInput from "@/components/elements/FloatingInput";
import Select2 from '@/components/elements/select2'
import GreenButton from "@/components/elements/green-button";
import AjaxTable from "@/components/elements/AjaxTable";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import Loading from "@/components/elements/loading";
import SmallColoredCheck from "@/components/elements/SmallColoredCheck";
import {UrlParamsMixin} from "@/mixins/UrlParamsMixin";
import _isNil from "lodash";
import AlarmProfileEdit from "@/components/alarmProfiles/alarmProfilesEdit";
import _ from "lodash";
import NotificationModal from "@/components/elements/NotificationModal.vue";
import errorMessage from "@/components/ErrorMessage.vue";

export default {
  name: "AlarmProfileIndex",
  mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin, UrlParamsMixin],
  components: {
      NotificationModal, SmallColoredCheck, AlarmProfileEdit,
      Loading, AjaxTable, GreenButton, Pagination, ItemsPerPageSelect, FloatingInput, Select2
  },
  props: {
    users: Object,
    admins: Array,
  },
  data() {
    return {
      data: [],
      headers: [
        {
          name: 'Alarm Profile Name',
          sortBy: 'name',
          show: 'sm',
          key: 'name',
        },
        {
          name: 'Threshold Alarming',
          sortBy: '',
          show: 'md',
          key: 'threshold_alarming',
        },
        {
          name: 'Offline Alarming',
          sortBy: '',
          show: 'md',
          key: 'threshold_alarming',
        },
        {
          name: 'Number of Points',
          sortBy: '',
          show: 'md',
          key: 'users',
        },
        {
          name: '',
          sortBy: '',
          show: 'md',
          key: 'actions',
        },
      ],
      filterHeaders: [
        {
          text: 'All Alarm Profiles',
          event: 'all-profiles',
        },
        {
          text: 'Public Alarm Profiles Only',
          event: 'public-profiles',
        },
      ],
      requestNumber: 0,

      // For Modal
      showAlarmProfile: false,
      showConfirmModal: false,
      showResultModal: false,
      showErrorModal: false,
      showFailedToDeleteModal: false,
      modalText: '',
      selectedAlarmProfile: null,
      selectedAlarmProfileIndex: null,
      preLoadAlarmProfile: null,

      alarmProfileIdToDelete: null,

      activeHeaderFilter: null,
      showPublicOnly: false,
    }
  },
  created() {
  },
  mounted() {
    this.preLoadAlarmProfile = this.urlParamsGet('alarmProfile')
  },
  methods: {
    getData() {
      this.ajaxTable.loading = true
      this.requestNumber++
      let params = JSON.parse(JSON.stringify(this.ajaxTable))
      params.requestNumber = this.requestNumber
      params.publicOnly = this.showPublicOnly
      //TODO update this to the proper alarm profiles route
      axios.get(this.company.buildCompanyApiInternalRoute('alarm-profiles'),
          {
            params: params,
          }
      ).then(response => {
        let data = response.data;
        if (data.meta.requestNumber !== `${this.requestNumber}`) {
          return
        }
        this.ajaxUpdateTableData(data); // This sends the data to the mixin for AjaxTable

        this.data = !_.isNil(data.data) ? data.data : [];
        this.allData = this.data;
        this.publicOnlyData = this.data.filter(profile => profile.private != true);


      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.ajaxTable.loading = false
      })
    },
    editAlarmProfileLink(alarmProfileId) {
      if (!this.permissions.can('edit_alarm_profiles')) {
        return '#'
      }
      window.location.href = this.company.buildCompanyRoute(`alarm-profiles/${alarmProfileId}/edit`)
    },
    viewAlarmProfileLink(alarmProfileId) {
      if (!this.permissions.can('view_alarm_profiles')) {
        return '#'
      }
      window.location.href = this.company.buildCompanyRoute(`alarm-profiles/${alarmProfileId}`)
    },
    createAlarmProfile(){
      this.selectedAlarmProfile = {
        'name': '',
        'id': null,
        'description': '',
      };
    },
    getUserName(userId){
      if(this.users.hasOwnProperty(userId)){
        return this.users[userId];
      }
      return null;
    },
    getUserAdminStatus(userId){
      return this.admins.includes(userId);
    },
    updateCurrentFilter(currentFilter) {
      this.page = 1
      this.activeHeaderFilter = currentFilter
      this.showPublicOnly = currentFilter === 'public-profiles'
      if(this.showPublicOnly){
        if (window.fathomLoaded) {
          window.fathom.trackGoal("ZOPXLG7I", 0);
        }
      }
      this.getData()
    },
    askToDelete(alarmProfileId, meterCount){
      if(meterCount > 0){
        this.showErrorModal = true
      }
      else {
        this.alarmProfileIdToDelete = alarmProfileId;
        this.showConfirmModal = true;
      }
    },
    alarmProfileThresholdHigh(row) {
      if(_.isNil(row.high_alarm_time) && row.high_alarm_instant === false){
        return false;
      }
      return row.high_alarm_value;
    },
    alarmProfileThresholdLow(row) {
      if(_.isNil(row.low_alarm_time) && row.low_alarm_instant === false){
        return false;
      }
      return row.low_alarm_value;
    },
    alarmProfileCautionThresholdHigh(row) {
      if(_.isNil(row.high_caution_time) && row.high_caution_instant === false){
        return false;
      }
      return row.high_caution_value;
    },
    alarmProfileCautionThresholdLow(row) {
      if(_.isNil(row.low_caution_time) && row.low_caution_instant === false){
        return false;
      }
      return row.low_caution_value;
    },
    alarmProfileValueString(first, second){
      // Javscript evaluates 0 === false as true.  This prevents the zero from being returned here.
      if(first === 0){
        first = first.toString();
      }
      if(second === 0){
        second = second.toString();
      }
      if(first && second){
        return first + ", " + second;
      }
      if(first){
        return first;
      }
      if(second){
        return second;
      }
      return '';
    },
    trash() {
      axios.delete(this.company.buildCompanyApiInternalRoute('alarm-profiles') + '/' + this.alarmProfileIdToDelete, {
      }).then(response => {
            this.modalText = 'Alarm Profile Deleted successfully!';
            this.alarmProfileIdToDelete = null;
            this.modalText = "Alarm deleted";
            this.showResultModal = true;
          }
      ).catch(error => {
        let data = error.response.data
        this.showFailedToDeleteModal = true
        this.modalText = data.message
      });
    },
  },
  computed: {
      errorMessage() {
          return errorMessage
      }
  },
  watch: {
    showAlarmProfile(val) {
      if (!val) {
        this.urlParamsRemove('alarmProfile')
      }
    }
  },
}
</script>

<style scoped>

</style>